'use client';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import type { SxProps, Theme } from '@mui/material/styles';

type EnvColor = 'warning' | 'info' | 'secondary' | 'error' | 'default';

interface EnvironmentDescriptor {
  label: string;
  color: EnvColor;
}

const PRODUCTION_VALUES = new Set(['production', 'prod']);

function resolveEnvironment(raw: string | undefined): EnvironmentDescriptor | null {
  const value = (raw ?? '').trim().toLowerCase();
  if (!value || PRODUCTION_VALUES.has(value)) return null;

  if (value === 'development' || value === 'dev' || value === 'local') {
    return { label: 'DEVELOPMENT', color: 'warning' };
  }
  if (value === 'staging' || value === 'stg') {
    return { label: 'STAGING', color: 'info' };
  }
  if (value === 'qa' || value === 'test') {
    return { label: value.toUpperCase(), color: 'secondary' };
  }
  if (value === 'preview') {
    return { label: 'PREVIEW', color: 'secondary' };
  }
  return { label: value.toUpperCase(), color: 'default' };
}

const PALETTE_BY_COLOR: Record<EnvColor, (theme: Theme) => string> = {
  warning: (theme) => theme.palette.warning.main,
  info: (theme) => theme.palette.info.main,
  secondary: (theme) => theme.palette.secondary.main,
  error: (theme) => theme.palette.error.main,
  default: (theme) => theme.palette.grey[600],
};

export type EnvironmentBadgeVariant = 'chip' | 'dot';

export interface EnvironmentBadgeProps {
  variant?: EnvironmentBadgeVariant;
  sx?: SxProps<Theme>;
}

export function EnvironmentBadge({ variant = 'chip', sx }: EnvironmentBadgeProps) {
  // Direct property access, not a bracket lookup: this is a client component,
  // and Next.js only inlines `NEXT_PUBLIC_*` into the browser bundle when it
  // can see the read statically. A dynamic lookup resolves to `undefined` at
  // runtime and the badge disappears everywhere.
  const env = resolveEnvironment(process.env.NEXT_PUBLIC_DEPLOY_ENV);
  if (!env) return null;

  if (variant === 'dot') {
    return (
      <Box
        role="status"
        aria-label={`Environment: ${env.label}`}
        sx={[
          (theme) => ({
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: PALETTE_BY_COLOR[env.color](theme),
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: theme.shadows[1],
          }),
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      />
    );
  }

  return (
    <Chip
      label={env.label}
      color={env.color === 'default' ? undefined : env.color}
      size="small"
      aria-label={`Environment: ${env.label}`}
      sx={[
        {
          fontWeight: 700,
          letterSpacing: 0.5,
          height: 20,
          fontSize: '0.625rem',
          '& .MuiChip-label': { px: 0.875 },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}
